
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ZoekService, IFullTextSearchResultItem, IFullTextSearchArgs, IPeriodeFilter } from '../zoek.service';

import { ActivatedRoute } from '@angular/router';
import { ICodeIdOmschrijving, GlobalDataService } from '../../shared/global-data.service';

import { Observable, Subject } from 'rxjs';
import { map, mergeMap, switchMap, take, shareReplay } from 'rxjs/operators';

import { jumpToAnchor } from '../../shared/helpers';
import { MenuService } from '../../shared/menu.service';
import { AppService } from '../../shared/app.service';
import { ISearchResult } from 'src/app/shared/state';
import { createCmdFactory } from '@cevi/rxjscommands';
import { getDefaultSearchArgs, ICommonSearchArgs } from 'src/app/shared/common-search-args';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { FormsModule } from '@angular/forms';
import { NgIf, NgFor, AsyncPipe, DatePipe } from '@angular/common';

export interface ISoortZitting {
  Id: number;
  Omschrijving: string;
}

interface IZoekVm {
  soortenZitting: ICodeIdOmschrijving[];
  zoekArg: ICommonSearchArgs;
  zoekResults: IFullTextSearchResultItem[];
  periodeFilters: IPeriodeFilter[];
  hasMoreResults: boolean;
}



@Component({
    selector: 'app-zoek',
    templateUrl: './zoek.component.html',
    styleUrls: ['./zoek.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, FormsModule, NgFor, AsyncPipe, DatePipe, HighlightPipe],
    providers:[ZoekService]
})
export class ZoekComponent implements OnInit {

  vm$: Observable<IZoekVm>;
  vm: IZoekVm;

  zoekSubject = new Subject<boolean>();
  zoekMeerSubject = new Subject<boolean>();
  zoekArgResetSubject = new Subject<boolean>();
  zoekNotitiesSubject = new Subject<boolean>();
  resetFiltersSubject = new Subject<boolean>();

  constructor(private menuService: MenuService, private appService: AppService,
    private globalDataService: GlobalDataService,
    private zoekService: ZoekService, private route: ActivatedRoute) {

    const emptyVm: IZoekVm = {
      periodeFilters: this.zoekService.getPeriodeFilters(),
      zoekArg: getDefaultSearchArgs(),
      zoekResults: [],
      hasMoreResults: false,
      soortenZitting: []
    };

    this.appService.routing.createCurrentHistoryItem('Zoekresultaten');

    const cmdFactory = createCmdFactory<IZoekVm>();
    cmdFactory.addMapAction(this.getSoortenZittingAction$, 'getSoortenZitting');
    cmdFactory.addMapAction(this.navigateFromZoekSubAction$, 'navigateFromZoekSubj');
    cmdFactory.addMapAction(this.navigateFromZoekMeerAction$, 'navigateFromZoekMeer');
    cmdFactory.addMapAction(this.resetFilterAction$, 'resetFilters');
    cmdFactory.addMapAction(this.searchResultsFromQueryParamsAction$, 'searchFromQueryParams');
    cmdFactory.addMapAction(this.refreshZoekresultatenAction$, 'refreshZoekresultaten');
    cmdFactory.addMapAction(this.restoreScrollPositionAfterFirstNavigation$, 'restoreScrollPositionAfterFirstNavigation');
    this.vm$ = cmdFactory.merge(emptyVm);
  }

  private getSoortenZittingAction$ = this.globalDataService.getSoortenZitting().pipe(
    map(soortenZitting => (vm: IZoekVm) => ({ ...vm, soortenZitting }))
  );

  private navigateFromZoekSubAction$ = this.globalDataService.getSoortenZitting().pipe(
    switchMap(() => this.zoekSubject), // make sure soorten zitting is loaded
    map(_ => (vm: IZoekVm) => {
      if (!vm.zoekArg.SearchFor) { return vm; }
      // CurrentPage is enkel gebruikt om de query parameters te wijzigen om navigatie te triggeren
      // voor het request wordt uiteindelijk stateOptions.page gebruikt.
      const newZoekArg: IFullTextSearchArgs = { ...vm.zoekArg, CurrentPage: 0 };
      this.appService.routing.navigateToZoekAgendapunten(newZoekArg, { addToHistory: false, concat: false, pageSize: 20, page: 0 });
      return vm;
    })
  );

  private navigateFromZoekMeerAction$ = this.zoekMeerSubject.pipe(
    map(_ => (vm: IZoekVm) => {
      // CurrentPage is enkel gebruikt om de query parameters te wijzigen om navigatie te triggeren
      // voor het request wordt uiteindelijk stateOptions.page gebruikt.
      const page = (+vm.zoekArg.CurrentPage + 1);
      const newZoekArg = { ...vm.zoekArg, CurrentPage: page };
      this.appService.routing.navigateToZoekAgendapunten(newZoekArg, { addToHistory: false, concat: true, pageSize: 20, page });
      return vm;
    })
  );

  private resetFilterAction$ = this.resetFiltersSubject.pipe(
    map(_ => (vm: IZoekVm) => {
      const newZoekArg: ICommonSearchArgs = { ...vm.zoekArg, SearchFor: '', SoortZittingId: null, CurrentPage: 0, PeriodeFilter: 'y', VanJaar: null, TotJaar: null, CombineWordsWith: ' or ' };
      this.appService.routing.navigateToZoekAgendapunten(newZoekArg, { addToHistory: false, concat: false, pageSize: 20 });
      return vm;
    })
  );

  private searchResultsFromQueryParamsAction$ = this.route.paramMap.pipe(
    map(paramMap => this.appService.routing.paramMapToSearchArgs(paramMap, getDefaultSearchArgs())),
    switchMap(zoekArg => this.zoekService.zoek(zoekArg).pipe(map((zoekResults) => ({ zoekArg, zoekResults })))),
    shareReplay(1),
    map(data => (vm: IZoekVm) => {
      this.appService.routing.setBackStateOptions({ allowFromCache: true, page: data.zoekArg.CurrentPage, pageSize: 20 });
      let lastPuntId = -1;
      let lastZittingId = -1;
      data.zoekResults.items.forEach(r => {
        r.Id = r.AgendapuntId + '_' + (r.DocumentAgendapuntId || '');
        r.isZittingBreak = r.ZittingId !== lastZittingId;
        r.isPuntBreak = r.AgendapuntId !== lastPuntId;
        lastPuntId = r.AgendapuntId;
        lastZittingId = r.ZittingId;
        r.Ext = r.Verwijzing ? r.Verwijzing.substring(r.Verwijzing.lastIndexOf('.') + 1, r.Verwijzing.length) : '';
      });
      return { ...vm, hasMoreResults: data.zoekResults.hasMoreResults, zoekResults: data.zoekResults.items, zoekArg: data.zoekArg } as IZoekVm;
    })
  );

  private restoreScrollPositionAfterFirstNavigation$ = this.searchResultsFromQueryParamsAction$.pipe(
    take(1),
    mergeMap(_=>this.route.fragment),
    map(fragment => (vm:IZoekVm)=>{
      jumpToAnchor(fragment);
      return vm;
    })
  )

  getSearchResultsFromQueryParams$() {
    return this.route.paramMap.pipe(
      map(paramMap => this.appService.routing.paramMapToSearchArgs(paramMap, getDefaultSearchArgs())),
      switchMap(zoekArg => this.zoekService.zoek(zoekArg).pipe(map((zoekResults) => ({ zoekArg, zoekResults })))),
      shareReplay(1)
    );
  }

  private refreshZoekresultatenAction$ = this.appService.refresh$.pipe(
    map(_ => (vm: IZoekVm) => {
      if (!vm.zoekArg.SearchFor) { return vm; }
      // CurrentPage is enkel gebruikt om de query parameters te wijzigen om navigatie te triggeren
      // voor het request wordt uiteindelijk stateOptions.page gebruikt.
      const newZoekArg: IFullTextSearchArgs = { ...vm.zoekArg, CurrentPage: 0 };
      this.appService.routing.navigateToZoekAgendapunten(newZoekArg, { addToHistory: false, concat: false, pageSize: 20, page: 0 });
      return vm;
    })
  );

  mapResultsToVm = (vm: IZoekVm, data: { zoekArg: ICommonSearchArgs, zoekResults: ISearchResult<IFullTextSearchResultItem> }) => {
    this.appService.routing.setBackStateOptions({ allowFromCache: true, page: data.zoekArg.CurrentPage, pageSize: 20 });
    let lastPuntId = -1;
    let lastZittingId = -1;
    data.zoekResults.items.forEach(r => {
      r.Id = r.AgendapuntId + '_' + (r.DocumentAgendapuntId || '');
      r.isZittingBreak = r.ZittingId !== lastZittingId;
      r.isPuntBreak = r.AgendapuntId !== lastPuntId;
      lastPuntId = r.AgendapuntId;
      lastZittingId = r.ZittingId;
      r.Ext = r.Verwijzing ? r.Verwijzing.substring(r.Verwijzing.lastIndexOf('.') + 1, r.Verwijzing.length) : '';
    });
    return { ...vm, hasMoreResults: data.zoekResults.hasMoreResults, zoekResults: data.zoekResults.items, zoekArg: data.zoekArg } as IZoekVm;
  }

  getZoekResultId = (_: number, result: IFullTextSearchResultItem) => result.Id;

  // @ViewChild('zoekresults') zoekresult:ElementRef;

  navigateToZoekDocumentenZitting(searchArg: ICommonSearchArgs) {
    this.appService.routing.navigateToZoekDocumentenZitting(searchArg);
  }
  navigateToZoekNotities(searchArg: ICommonSearchArgs) {
    this.appService.routing.navigateToZoekNotities(searchArg);
  }
  navigateToDetailPunt(result: IFullTextSearchResultItem) {
    this.appService.routing.setFragment(`p_${result.AgendapuntId}`);
    this.appService.routing.navigateToDetailPunt(result.ZittingId, result.AgendapuntId);
    return false;
  }
  navigateToDetailDocument = (item: IFullTextSearchResultItem) => {
    this.menuService.openDocument('agendapunt', item.DocumentAgendapuntId, item.DocumentTitel, item.Verwijzing);
    return false;
  }
  wisZoekFilters=()=>{
    this.resetFiltersSubject.next(true)
    return false;
  }


  ngOnInit() {
    // test om auto load meer result bij scrollen
    // dit werkt nog niet zoals het hoort: bij terugkeren van detail is hasMoreResults terug true
    // fromEvent(this.zoekresult.nativeElement,'scroll').pipe(
    //   debounceTime(300),
    //   filter(_=>this.hasMoreResults && (this.zoekresult.nativeElement.offsetHeight + this.zoekresult.nativeElement.scrollTop === this.zoekresult.nativeElement.scrollHeight) )
    // ).subscribe(_=>this.zoekMeer());
  }

}
